import * as React from 'react';
import PropTypes from 'prop-types';

import {
    Hidden, Drawer, Menu, IconButton, makeStyles,
} from '@material-ui/core';

import MenuContent from './MenuContent';
import Header from './Header';
import { Loading, ErrorMessage, PageNotFound, ErrorBoundary } from '../../youwell-common/components';

import * as AuthStore from '../../app/store/AuthStore';
import { scrollerWidth, headerHeight } from '../../app/style/theme';
import { CloseIcon } from '../icons';
import { useYouwellDispatch, useYouwellSelector } from '../../app/store';
import Footer from './Footer';

export const contentWidth = 1080;

export const useStyles = makeStyles(theme => ({
    root: {
        background: theme.palette.background.default,
        display: 'flex',
        justifyContent: 'center',
    },

    header: {
        position: 'fixed',
        width: '100vw',
        paddingRight: scrollerWidth,
        height: headerHeight,
        background: theme.palette.primary.main,
        display: 'flex',
        justifyContent: 'center',
        zIndex: 100, // must be higher than QuestionnaireTable-stickyHeaderCell

        [theme.breakpoints.down('xs')]: {
            paddingRight: 0,
        },

        '@media print': {
            display: 'none',
        },
    },
    headerContent: {
        width: '100%',
        maxWidth: contentWidth,

        [theme.breakpoints.up('lg')]: {
            width: contentWidth - scrollerWidth,
        },
    },

    errorMessage: {
        maxWidth: 720, // max width for content with side menu..
        padding: theme.spacing(2, 4, 0),

        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2, 4.5, 0),
        },

        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1, 1.5, 0),
        },
    },
    content: {
        marginTop: headerHeight,
        width: '100%',
        minHeight: '100vh',
        maxWidth: contentWidth,
        // background: theme.palette.common.white,
        // boxShadow: theme.shadows[1],

        [theme.breakpoints.up('lg')]: {
            width: contentWidth - scrollerWidth,
        },

        [theme.breakpoints.down('xs')]: {
            boxShadow: 'none',
            minHeight: 0,
        },

        '@media print': {
            marginTop: 0,
            minHeight: 0,
        },
    },
    mainContent: {
    },
    closeButton: {
        position: 'absolute',
        right: 8,
        top: 8,
        padding: theme.spacing(1),
        zIndex: 1,
    },
    menu: {
        background: theme.palette.common.white,
    },
}));

const Layout = ({
    children, hideHeaderLinks, hideMenu, headerPageTitle, hideAppTitle, muteErrors,
}) => {
    const dispatch = useYouwellDispatch();
    const classes = useStyles();

    const loading = useYouwellSelector(state => state.auth.loading || state.programStore.loading || state.user.loading || state.publicStore.loading || state.previewStore.loading || state.requestStatusStore.loading);
    const errorMessage = useYouwellSelector(state => [].concat(state.auth.errors, state.programStore.errors, state.user.errors, state.publicStore.errors, state.previewStore.errors, state.requestStatusStore.errors).filter(val => val).join('<br/>'));
    const pageNotFound = useYouwellSelector(state => state.pageSettings.pageNotFound);

    const [menuAnchorElement, setMenuAnchorElement] = React.useState(null);

    const openMenu = (event) => {
        setMenuAnchorElement(event.currentTarget);
    }

    const closeMenu = () => {
        setMenuAnchorElement(null);
    }

    const clearErrors = () => {
        dispatch(AuthStore.actionCreators.clearError());
    }

    const navigateMenu = (event) => {
        const key = event.key;
        if (key === 'Escape' || key === 'Backspace') {
            closeMenu();
        }
    }

    return (
        <>
            <div className={classes.root}>
                <ErrorBoundary id="Layout_Header">
                    <div className={classes.header}>
                        <div className={classes.headerContent}>
                            <Header
                                onOpenMenu={openMenu}
                                hideLinks={hideHeaderLinks}
                                hideMenu={hideMenu}
                                hideAppTitle={hideAppTitle}
                                pageTitle={headerPageTitle}
                            />
                        </div>
                    </div>
                </ErrorBoundary>

                <div className={classes.content}>
                    <ErrorBoundary id="Layout_Content">
                        {pageNotFound ? (
                            <PageNotFound />
                        ) : (
                            <>
                                <div><ErrorMessage className={classes.errorMessage} message={errorMessage} onClear={clearErrors} muteErrors={muteErrors} scrollMarginTop={headerHeight} /></div>
                                <div className={classes.mainContent}>
                                    {children}
                                </div>
                            </>
                        )}
                    </ErrorBoundary>
                </div>
            </div>

            <ErrorBoundary id="Layout_Footer">
                <Footer />
            </ErrorBoundary>

            <ErrorBoundary id="Layout_Loading">
                <Loading loading={loading} />
            </ErrorBoundary>

            <ErrorBoundary id="Layout_SideMenu">
                <Hidden implementation="js" smUp>
                    <Drawer anchor="right" open={!!menuAnchorElement} onClose={closeMenu}>
                        <div
                            tabIndex={0}
                            role="button"
                            onClick={closeMenu}
                            onKeyDown={navigateMenu}
                            className={classes.menu}
                        >
                            <IconButton className={classes.closeButton}><CloseIcon /></IconButton>
                            <MenuContent />
                        </div>
                    </Drawer>
                </Hidden>

                <Hidden implementation="js" xsDown>
                    <Menu
                        id="app-menu"
                        classes={{ paper: classes.menu }}
                        anchorEl={menuAnchorElement}
                        open={!!menuAnchorElement}
                        onClose={closeMenu}
                        onClick={closeMenu}
                        onKeyDown={navigateMenu}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        getContentAnchorEl={null}
                        disableAutoFocusItem
                    >
                        <MenuContent />
                        <IconButton className={classes.closeButton}><CloseIcon /></IconButton>
                    </Menu>
                </Hidden>
            </ErrorBoundary>
        </>
    );
}

Layout.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
    hideHeaderLinks: PropTypes.bool,
    hideMenu: PropTypes.bool,
    hideAppTitle: PropTypes.bool,
    headerPageTitle: PropTypes.string,
    muteErrors: PropTypes.array,
};

Layout.defaultProps = {
    children: null,
    hideHeaderLinks: false,
    hideMenu: false,
    hideAppTitle: false,
    headerPageTitle: null,
    muteErrors: null,
};

export default Layout;
